import { useSearchParams } from 'react-router'
import type { Route } from './+types/MaterialsPage'
import { PageShell } from '@/components/layout/PageShell'
import { MaterialCard } from '@/components/app/MaterialCard'
import { LocaleLink as Link } from '@/components/LocaleLink'
import { useCategories, useMaterials } from '@/lib/cms/hooks'
import { createContentLoader } from '@/lib/cms/prefetch.server'
import { contentQueries } from '@/lib/cms/queries'
import { useTranslation } from '@/lib/i18n/LocaleContext'
import { messages } from '@/lib/i18n/messages'
import { localeFromParams } from '@/lib/routeLocale'
import { buildMeta, type PageSeo } from '@/lib/seo'

const CATEGORY_PARAM = 'kategori'

export async function loader({ params, url }: Route.LoaderArgs) {
  const locale = localeFromParams(params)
  const category = url.searchParams.get(CATEGORY_PARAM) ?? undefined
  const content = createContentLoader(locale)
  await content.prefetch([contentQueries.materials(category), contentQueries.categories()])
  const t = messages[locale].app
  const seo: PageSeo = { title: t.materialsTitle, description: t.materialsLede }
  return { dehydratedState: content.dehydrate(), seo }
}

export const meta: Route.MetaFunction = (args) => buildMeta(args, args.data?.seo)

export default function MaterialsPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const active = searchParams.get(CATEGORY_PARAM) ?? undefined
  const { data: categories } = useCategories()
  const { data: materials, loading, error } = useMaterials(active)

  return (
    <PageShell>
      <section className="block-tight">
        <div className="wrap">
          <h1 className="h1">{t.app.materialsTitle}</h1>
          <p className="lede">{t.app.materialsLede}</p>

          <nav className="chips" aria-label={t.app.materialsTitle}>
            <Link to="/materialer" className="chip" aria-current={!active ? 'page' : undefined}>{t.app.allCategories}</Link>
            {(categories ?? []).map((c) => (
              <Link
                key={c.id}
                to={`/materialer?${CATEGORY_PARAM}=${encodeURIComponent(c.slug)}`}
                className="chip"
                aria-current={active === c.slug ? 'page' : undefined}
              >
                {c.name}
              </Link>
            ))}
          </nav>

          {loading && <p className="muted">{t.common.loading}</p>}
          {error && <p className="error-text">{t.common.error}</p>}
          {materials && materials.length === 0 && <p className="muted">{t.app.noMaterials}</p>}
          {materials && materials.length > 0 && (
            <ul className="card-list">
              {materials.map((m) => <li key={m.id}><MaterialCard material={m} /></li>)}
            </ul>
          )}
        </div>
      </section>
    </PageShell>
  )
}
